import { useCallback, useLayoutEffect, useRef, type MutableRefObject, type RefObject } from 'react';

import {
    CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS,
    CHART_TO_TABLE_SCROLL_SYNC_GUARD_MS,
    TABLE_TO_CHART_SCROLL_SYNC_GUARD_MS,
    getMaxHorizontalScrollLeft,
} from './packArchitectureTable.helpers';

type UsePackArchitectureChartToTableScrollSyncParams = {
    chartScrollRef: RefObject<HTMLDivElement | null>;
    tableWrapperRef: RefObject<HTMLDivElement | null>;
    isSyncingTableFromChartRef: MutableRefObject<boolean>;
    /** Set true while programmatically moving the chart from table scroll (skip chart_scrolled). */
    isSyncingChartFromTableRef: MutableRefObject<boolean>;
    suppressChartToTableSyncRef: MutableRefObject<boolean>;
    chartColumnCount: number;
    chartColumnKeys?: string[];
    isChartPaginatedLoading: boolean;
    isTablePaginatedLoading: boolean;
    tableLoadedColumnCount: number;
    tableColumnKeys?: string[];
    /** Re-attach when chart columns change or table first mounts — not on table column pagination. */
    tableSyncKey: number;
};

/**
 * One-way chart → table scroll sync (Price Ladder pattern).
 * useLayoutEffect + scrollTo({ behavior: 'auto' }) keeps table in step while chart scrolls.
 */
// @code-guard: skip-next-function reason="Legacy scroll-sync hook coordinates bidirectional pagination behavior and is planned for incremental extraction."
export function usePackArchitectureChartToTableScrollSync({
    chartScrollRef,
    tableWrapperRef,
    isSyncingTableFromChartRef,
    isSyncingChartFromTableRef,
    suppressChartToTableSyncRef,
    chartColumnCount,
    isChartPaginatedLoading,
    isTablePaginatedLoading,
    tableLoadedColumnCount,
    tableSyncKey,
}: UsePackArchitectureChartToTableScrollSyncParams) {
    const syncGuardTimeoutRef = useRef<number | null>(null);
    const chartSyncGuardTimeoutRef = useRef<number | null>(null);
    const previousChartColumnCountRef = useRef(chartColumnCount);
    const previousTableLoadedColumnCountRef = useRef<number | null>(null);
    const chartToTableRafRef = useRef<number | null>(null);
    const tableToChartRafRef = useRef<number | null>(null);
    const cachedTableScrollElementsRef = useRef<{
        body: HTMLElement | null;
        header: HTMLElement | null;
        headerContent: HTMLElement | null;
    }>({
        body: null,
        header: null,
        headerContent: null,
    });
    const lastTableSyncScrollLeftRef = useRef<number | null>(null);
    const lastChartSyncScrollLeftRef = useRef<number | null>(null);

    const resolveTableScrollElements = useCallback(() => {
        const tableWrapper = tableWrapperRef.current;
        if (!tableWrapper) {
            cachedTableScrollElementsRef.current = {
                body: null,
                header: null,
                headerContent: null,
            };
            return cachedTableScrollElementsRef.current;
        }

        const cached = cachedTableScrollElementsRef.current;
        const isBodyUsable =
            !!cached.body && cached.body.isConnected && tableWrapper.contains(cached.body);
        const isHeaderUsable =
            !cached.header || (cached.header.isConnected && tableWrapper.contains(cached.header));
        const isHeaderContentUsable =
            !cached.headerContent ||
            (cached.headerContent.isConnected && tableWrapper.contains(cached.headerContent));

        if (isBodyUsable && isHeaderUsable && isHeaderContentUsable) {
            return cached;
        }

        const body = tableWrapper.querySelector('.ant-table-body') as HTMLElement | null;
        const header =
            (tableWrapper.querySelector('.ant-table-header') as HTMLElement | null) ??
            (tableWrapper.querySelector('.pack-arch-vtable-header-scroll') as HTMLElement | null);
        const headerContent =
            (header?.querySelector('.ant-table-content') as HTMLElement | null) ??
            (tableWrapper.querySelector('.pack-arch-vtable-header-scroll') as HTMLElement | null);

        cachedTableScrollElementsRef.current = {
            body,
            header,
            headerContent,
        };
        return cachedTableScrollElementsRef.current;
    }, [tableWrapperRef]);

    useLayoutEffect(() => {
        // Pagination/column append can re-mount Ant table internals.
        // Drop stale refs so the next sync resolves live nodes.
        cachedTableScrollElementsRef.current = {
            body: null,
            header: null,
            headerContent: null,
        };
        lastTableSyncScrollLeftRef.current = null;
        lastChartSyncScrollLeftRef.current = null;
    }, [tableLoadedColumnCount, tableSyncKey]);

    const syncTableScrollLeft = useCallback(
        (scrollLeft: number) => {
            const { body, header, headerContent } = cachedTableScrollElementsRef.current.body
                ? cachedTableScrollElementsRef.current
                : resolveTableScrollElements();
            if (!body) return;

            const maxScrollLeft = getMaxHorizontalScrollLeft(body);
            const clampedScrollLeft = Math.min(Math.max(0, scrollLeft), maxScrollLeft);

            if (
                lastTableSyncScrollLeftRef.current !== null &&
                Math.abs(lastTableSyncScrollLeftRef.current - clampedScrollLeft) < 0.5
            ) {
                return;
            }

            body.scrollLeft = clampedScrollLeft;
            if (headerContent) headerContent.scrollLeft = clampedScrollLeft;
            if (header) header.scrollLeft = clampedScrollLeft;
            lastTableSyncScrollLeftRef.current = clampedScrollLeft;
        },
        [resolveTableScrollElements],
    );

    const syncTableFromChartNow = useCallback(() => {
        if (suppressChartToTableSyncRef.current || isSyncingChartFromTableRef.current) return;

        const chartElement = chartScrollRef.current;
        if (!chartElement) return;

        if (syncGuardTimeoutRef.current !== null) {
            window.clearTimeout(syncGuardTimeoutRef.current);
        }

        const { body: tableBody } = resolveTableScrollElements();
        if (!tableBody) return;
        isSyncingTableFromChartRef.current = true;
        const chartMaxScrollLeft = getMaxHorizontalScrollLeft(chartElement);
        const tableMaxScrollLeft = getMaxHorizontalScrollLeft(tableBody);
        // Keep chart/table columns aligned by mirroring absolute horizontal offset.
        // Ratio-based sync drifts when viewport widths differ (common here because
        // chart and table reserve different fixed/sticky side panels).
        const targetScrollLeft = chartElement.scrollLeft;
        let shouldKickTablePagination = false;

        if (Number.isFinite(targetScrollLeft)) {
            syncTableScrollLeft(targetScrollLeft);
            // If chart just gained additional width while table remains pinned to
            // its old right edge, proactively emit one more table scroll event so
            // table pagination can continue without requiring user movement.
            shouldKickTablePagination =
                chartMaxScrollLeft > tableMaxScrollLeft + 0.5 &&
                Math.abs(tableBody.scrollLeft - tableMaxScrollLeft) <= 1;
        }

        syncGuardTimeoutRef.current = window.setTimeout(() => {
            isSyncingTableFromChartRef.current = false;
            syncGuardTimeoutRef.current = null;
            if (shouldKickTablePagination && tableBody.isConnected) {
                tableBody.dispatchEvent(new Event('scroll'));
            }
        }, CHART_TO_TABLE_SCROLL_SYNC_GUARD_MS);
    }, [
        chartScrollRef,
        isSyncingChartFromTableRef,
        isSyncingTableFromChartRef,
        resolveTableScrollElements,
        suppressChartToTableSyncRef,
        syncTableScrollLeft,
    ]);

    const syncTableFromChart = useCallback(() => {
        if (chartToTableRafRef.current !== null) return;
        chartToTableRafRef.current = requestAnimationFrame(() => {
            chartToTableRafRef.current = null;
            syncTableFromChartNow();
        });
    }, [syncTableFromChartNow]);

    const syncChartFromTableNow = useCallback(() => {
        if (isSyncingTableFromChartRef.current || suppressChartToTableSyncRef.current) return;

        const chartElement = chartScrollRef.current;
        const { body: tableBody } = resolveTableScrollElements();
        if (!chartElement || !tableBody) return;

        const chartMaxScrollLeft = getMaxHorizontalScrollLeft(chartElement);
        const clampedTarget = Math.min(Math.max(0, tableBody.scrollLeft), chartMaxScrollLeft);
        const tableMaxScrollLeft = getMaxHorizontalScrollLeft(tableBody);
        const shouldKickChartPagination =
            tableMaxScrollLeft > chartMaxScrollLeft + 0.5 &&
            tableBody.scrollLeft >= tableMaxScrollLeft - 1 &&
            clampedTarget >= chartMaxScrollLeft - 1;
        if (
            lastChartSyncScrollLeftRef.current !== null &&
            Math.abs(lastChartSyncScrollLeftRef.current - clampedTarget) < 0.5 &&
            !shouldKickChartPagination
        ) {
            return;
        }

        // Timer-based guard (mirrors the chart→table direction above), not
        // "reset on next animation frame": the echo scroll event fired by
        // this write to `chartElement.scrollLeft` schedules its own handler
        // via rAF, and that rAF request can end up queued *after* an
        // in-callback rAF reset, so a single-frame guard would intermittently
        // clear itself before the echo arrives - letting a redundant
        // chart→table sync fire on every table scroll tick and double the
        // forced-layout work, which is what made table-driven scrolling feel
        // laggier than chart-driven scrolling.
        if (chartSyncGuardTimeoutRef.current !== null) {
            window.clearTimeout(chartSyncGuardTimeoutRef.current);
        }
        isSyncingChartFromTableRef.current = true;
        chartElement.scrollLeft = clampedTarget;
        lastChartSyncScrollLeftRef.current = clampedTarget;
        // Keep the flag past the chart's scroll rAF so Mixpanel does not treat sync as user scroll.
        chartSyncGuardTimeoutRef.current = window.setTimeout(() => {
            isSyncingChartFromTableRef.current = false;
            chartSyncGuardTimeoutRef.current = null;
            if (shouldKickChartPagination && chartElement.isConnected) {
                chartElement.dispatchEvent(new Event('scroll'));
            }
        }, TABLE_TO_CHART_SCROLL_SYNC_GUARD_MS);
    }, [
        chartScrollRef,
        isSyncingChartFromTableRef,
        isSyncingTableFromChartRef,
        resolveTableScrollElements,
        suppressChartToTableSyncRef,
    ]);

    const syncChartFromTable = useCallback(() => {
        if (tableToChartRafRef.current !== null) return;
        tableToChartRafRef.current = requestAnimationFrame(() => {
            tableToChartRafRef.current = null;
            syncChartFromTableNow();
        });
    }, [syncChartFromTableNow]);

    useLayoutEffect(() => {
        const chartElement = chartScrollRef.current;
        if (!chartElement) return undefined;
        resolveTableScrollElements();

        const previousTableColumns = previousTableLoadedColumnCountRef.current;
        const didTableAppendColumns =
            previousTableColumns !== null && tableLoadedColumnCount > previousTableColumns;
        previousTableLoadedColumnCountRef.current = tableLoadedColumnCount;

        chartElement.addEventListener('scroll', syncTableFromChart, { passive: true });

        if (!isTablePaginatedLoading && !suppressChartToTableSyncRef.current) {
            syncTableFromChart();
        }

        // When table appends new columns, chart may already be at right edge and stop
        // emitting native scroll events. Retry syncing from chart position so table
        // catches up for newly paginated columns.
        const tableAppendRetryTimeoutIds = didTableAppendColumns
            ? [...CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS, 450, 800].map((delayMs) =>
                  window.setTimeout(() => {
                      syncTableFromChart();
                  }, delayMs),
              )
            : [];

        return () => {
            chartElement.removeEventListener('scroll', syncTableFromChart);
            if (syncGuardTimeoutRef.current !== null) {
                window.clearTimeout(syncGuardTimeoutRef.current);
                syncGuardTimeoutRef.current = null;
            }
            tableAppendRetryTimeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [
        chartScrollRef,
        isTablePaginatedLoading,
        resolveTableScrollElements,
        suppressChartToTableSyncRef,
        syncTableFromChart,
        tableLoadedColumnCount,
        tableSyncKey,
    ]);

    useLayoutEffect(() => {
        const { body: tableBody } = resolveTableScrollElements();
        if (!tableBody) return undefined;

        tableBody.addEventListener('scroll', syncChartFromTable, { passive: true });
        return () => {
            tableBody.removeEventListener('scroll', syncChartFromTable);
            if (chartSyncGuardTimeoutRef.current !== null) {
                window.clearTimeout(chartSyncGuardTimeoutRef.current);
                chartSyncGuardTimeoutRef.current = null;
            }
        };
    }, [resolveTableScrollElements, syncChartFromTable, tableLoadedColumnCount, tableSyncKey, tableWrapperRef]);

    useLayoutEffect(() => {
        const didAppendColumns = chartColumnCount > previousChartColumnCountRef.current;
        previousChartColumnCountRef.current = chartColumnCount;

        if (!didAppendColumns || isChartPaginatedLoading) {
            return undefined;
        }

        const timeoutIds = [...CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS, 450, 800].map((delayMs) =>
            window.setTimeout(() => {
                syncTableFromChart();
            }, delayMs),
        );

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [chartColumnCount, isChartPaginatedLoading, suppressChartToTableSyncRef, syncTableFromChart]);

    useLayoutEffect(() => {
        return () => {
            if (chartToTableRafRef.current !== null) {
                cancelAnimationFrame(chartToTableRafRef.current);
                chartToTableRafRef.current = null;
            }
            if (tableToChartRafRef.current !== null) {
                cancelAnimationFrame(tableToChartRafRef.current);
                tableToChartRafRef.current = null;
            }
            if (chartSyncGuardTimeoutRef.current !== null) {
                window.clearTimeout(chartSyncGuardTimeoutRef.current);
                chartSyncGuardTimeoutRef.current = null;
            }
            isSyncingChartFromTableRef.current = false;
        };
    }, [isSyncingChartFromTableRef]);
}
